/**
 * Testi italiani dell'abbonamento e dei pagamenti (ADR 0027): pagina «Abbonamento e
 * pagamenti» e pagina di esito dopo il Checkout di Stripe.
 *
 * «Stripe» resta: è il nome di chi incassa, e l'utente lo vede nella pagina di
 * pagamento. Nessun nome interno (sessione, webhook, servizio) arriva a schermo.
 */

/** Testi dei pagamenti. */
export const billing = {
  /** Pagina «Abbonamento». */
  page: {
    /** Titolo della pagina, uguale alla voce di menu. */
    title: 'Abbonamento',
    /** Che cosa mostra. */
    description:
      'Il tuo piano, l’abbonamento e i minuti di voce. Paghi su Stripe: institor non vede i dati della carta.',
    /** Lettura non riuscita. */
    loadFailed: 'Lettura dei pagamenti non riuscita. Riprova.',
    /** Pagamenti spenti. */
    unavailable: 'Pagamenti non disponibili in questo momento. Riprova più tardi.',
    /** Ritorno da un Checkout abbandonato. */
    cancelled: 'Pagamento annullato. Non ti è stato addebitato nulla.',
  },
  /** Riquadro del piano attuale. */
  current: {
    /** Titolo del riquadro. */
    title: 'Piano attuale',
    /** Etichetta del piano. */
    plan: 'Piano',
    /** Etichetta dello stato dell'abbonamento. */
    status: 'Abbonamento',
    /** Nessun abbonamento. */
    none: 'Nessun abbonamento',
    /** Etichetta della data di rinnovo. */
    renewal: 'Prossimo rinnovo',
    /** Etichetta della data di fine, per un abbonamento disdetto. */
    endsOn: 'Termina il',
    /** Pulsante che apre il portale di Stripe. */
    manage: 'Gestisci abbonamento',
    /** Portale non aperto. */
    manageFailed: 'Apertura della gestione non riuscita. Riprova.',
    /** Piano scelto da un amministratore, che il pagamento non cambia. */
    adminOverride:
      'Il tuo piano l’ha assegnato un amministratore. Finché resta quell’assegnazione, l’abbonamento non lo cambia.',
    /** Addebito non riuscito. */
    pastDue: 'Ultimo addebito non riuscito. Aggiorna la carta da «Gestisci abbonamento» per non perdere il piano.',
    /** Disdetta a fine periodo. */
    cancelAtEnd: 'Abbonamento disdetto. Il piano resta attivo fino al {{date}}.',
    /** Amministratore della piattaforma. */
    unrestricted: 'Sei amministratore della piattaforma: nessun limite si applica a te.',
  },
  /** Stati di un abbonamento su Stripe. */
  statuses: {
    active: 'Attivo',
    trialing: 'In prova',
    past_due: 'Pagamento in ritardo',
    canceled: 'Cancellato',
    unpaid: 'Non pagato',
    incomplete: 'In attesa di pagamento',
    incomplete_expired: 'Scaduto',
    paused: 'In pausa',
  },
  /** Confronto dei piani. */
  plans: {
    /** Titolo della sezione. */
    title: 'Piani',
    /** Prezzo mensile. */
    perMonth: '{{price}} al mese',
    /** Etichetta del piano in uso. */
    current: 'Piano attuale',
    /** Pulsante d'acquisto. */
    choose: 'Scegli {{plan}}',
    /** Chi ha già un abbonamento cambia piano dal portale. */
    switchHint: 'Hai già un abbonamento: per cambiare piano usa «Gestisci abbonamento».',
    /** Che cosa dà ciascun piano, in breve. */
    features: {
      base: '1 set, fino a 3 progetti, 30 milioni di token al mese',
      premium: 'Fino a 3 set, 10 progetti per set, 100 milioni di token al mese',
    },
    /** Che cosa dà un piano con un set, dal listino in vigore. */
    featuresOne: '1 set, fino a {{projects}} progetti, {{tokens}} milioni di token al mese',
    /** Che cosa dà un piano con più set, dal listino in vigore. */
    featuresMany: 'Fino a {{sets}} set, {{projects}} progetti per set, {{tokens}} milioni di token al mese',
    /** Checkout non aperto. */
    checkoutFailed: 'Apertura del pagamento non riuscita. Riprova.',
    /** C'è già un abbonamento. */
    exists: 'Hai già un abbonamento attivo. Cambia piano da «Gestisci abbonamento».',
  },
  /** Paese e tipo di cliente, prima del pagamento. */
  buyer: {
    /** Etichetta del paese. */
    country: 'Paese di fatturazione',
    /** Testo nel campo vuoto. */
    countryPlaceholder: 'Scegli il paese',
    /** Aiuto sotto il campo. */
    countryHelp: 'Il paese in cui risiedi o ha sede la tua azienda. Decide le regole fiscali.',
    /** Valuta dei prezzi per paese (ADR 0064). */
    currencyNote: 'Nei paesi dell’area euro i prezzi sono in euro, negli altri in dollari.',
    /** Valuta fissata dall'abbonamento. */
    currencyLocked: 'I prezzi restano in {{currency}}: è la valuta del tuo abbonamento.',
    /** Nomi delle valute, come complemento. */
    currencies: { eur: 'euro', usd: 'dollari' },
    /** Paese mancante. */
    countryRequired: 'Scegli il paese di fatturazione per continuare.',
    /** Casella per chi compra come azienda. */
    business: 'Compro per un’azienda con partita IVA',
    /** Paese in cui si vende solo ad aziende. */
    businessOnly: 'In questo paese vendiamo solo ad aziende con partita IVA. Spunta la casella se compri per un’azienda.',
  },
  /** Casella obbligatoria prima del pagamento (termini di servizio, §5). */
  consent: {
    /** Testo della casella: deve restare uguale a CONSENT_TEXT del servizio billing. */
    label: 'Chiedo che il servizio inizi subito e so che, se recedo entro 14 giorni, pago la parte già usata.',
    /** Spiegazione sotto la casella. */
    detail: 'Serve per ogni acquisto. Senza recesso non sono previsti rimborsi.',
    /** Errore se si prova a pagare senza spuntarla. */
    required: 'Spunta la casella per continuare con il pagamento.',
  },
  /** Minuti dell'agente vocale. */
  voice: {
    /** Titolo della sezione. */
    title: 'Minuti di voce',
    /** Etichetta del saldo. */
    balance: 'Minuti disponibili',
    /** Pulsante di ricarica. */
    buy: 'Ricarica {{minutes}} minuti',
    /** Prezzo della ricarica. */
    price: '{{minutes}} minuti a {{price}}, una tantum. Non scadono.',
    /** Minuti del piano nel mese. */
    plan: 'Minuti del piano nel mese',
    /** Usati sul totale del piano. */
    planValue: '{{used}} usati su {{total}}',
    /** Senza limite. */
    unlimited: 'Senza limite',
    /** Minuti rimasti in tutto. */
    left: 'Rimasti in tutto',
    /** Voce spenta. */
    off: 'La voce non è attiva su questa installazione.',
  },
  /** Acquisti una tantum. */
  purchases: {
    /** Titolo della sezione. */
    title: 'Acquisti',
    /** Nessun acquisto. */
    empty: 'Nessun acquisto.',
    /** Colonne. */
    product: 'Prodotto',
    date: 'Data',
    amount: 'Importo',
    status: 'Stato',
    /** Nomi dei prodotti. */
    products: {
      voice_minutes_60: '60 minuti di voce',
      credits_1000: '1.000 crediti',
      credits_5500: '5.500 crediti',
      credits_24000: '24.000 crediti',
      credits: 'Ricarica dei crediti',
    },
    /** Stati di un acquisto. */
    statuses: {
      pending: 'In attesa di conferma',
      paid: 'Pagato',
      failed: 'Non riuscito',
      partially_refunded: 'Rimborsato in parte',
      refunded: 'Rimborsato',
      rejected: 'Respinto',
    },
  },
  /** Fatture emesse da Stripe (ADR 0030 §7). */
  overage: {
    /** Titolo della sezione. */
    title: 'Consumo a fine mese',
    /** Che cosa fa. */
    intro:
      'Quando finiscono i token, i minuti di voce o lo spazio del piano, il lavoro continua e paghi il consumo con la fattura del rinnovo. Il tetto ferma la spesa.',
    /** Spento sulla piattaforma. */
    unavailable: 'Il consumo a fine mese non è ancora attivo.',
    /** Senza abbonamento. */
    needsPlan: 'Il consumo a fine mese è per chi ha un abbonamento Base o Premium attivo.',
    /** Spento per una fattura non pagata. */
    paymentFailed: 'Una fattura non è stata pagata e il consumo si è spento. Aggiorna la carta e riaccendilo.',
    /** Interruttore. */
    toggle: 'Paga il consumo oltre i limiti a fine mese',
    /** Tetto. */
    cap: 'Tetto del mese',
    /** Aiuto del tetto. */
    capHelp: 'Arrivato al tetto, il lavoro oltre i limiti si ferma fino al rinnovo.',
    /** Pulsante del tetto. */
    saveCap: 'Salva tetto',
    /** Esito. */
    saved: 'Consumo a fine mese aggiornato.',
    /** Errore di lettura. */
    loadFailed: 'Consumo a fine mese non disponibile. Riprova.',
    /** Errore di salvataggio. */
    saveFailed: 'Modifica non riuscita. Riprova.',
    /** Tetto non valido. */
    capInvalid: 'Il tetto va da {{min}} a {{max}}.',
    /** Spesa. */
    spent: 'Spesa del periodo',
    /** Stima. */
    estimate: 'Stima a fine periodo',
    /** Prossima fattura. */
    invoiceOn: 'Fattura del consumo il {{date}}.',
    /** Prezzi. */
    prices: 'Prezzi, IVA esclusa: {{tokens}} ogni 100.000 token, {{voice}} al minuto di voce, {{storage}} per GiB al mese di spazio.',
    /** Voci. */
    items: {
      tokens: '{{count}} blocchi da 100.000 token',
      voice_minutes: '{{count}} minuti di voce',
      storage_gib_days: '{{count}} GiB al giorno di spazio',
    },
  },
  invoices: {
    /** Titolo della sezione. */
    title: 'Fatture',
    /** Nessuna fattura. */
    empty: 'Nessuna fattura.',
    /** Pagamenti spenti. */
    unavailable: 'Le fatture non sono disponibili.',
    /** Errore di lettura. */
    loadFailed: 'Fatture non disponibili. Riprova.',
    /** Colonne. */
    number: 'Numero',
    date: 'Data',
    amount: 'Importo',
    status: 'Stato',
    links: 'Documenti',
    /** Collegamento alla pagina della fattura. */
    view: 'Apri fattura {{number}}',
    /** Collegamento al PDF. */
    pdf: 'Scarica PDF {{number}}',
    /** Numero non ancora assegnato. */
    noNumber: 'senza numero',
    /** Stati di una fattura. */
    statuses: {
      open: 'Da pagare',
      paid: 'Pagata',
      uncollectible: 'Non riscossa',
      void: 'Annullata',
    },
    /** Dove si trova lo storico intero. */
    portalHint: 'Qui vedi le ultime fatture. Lo storico completo è nel portale di Stripe.',
    /** Spiegazione sopra «Chiedi la fattura». */
    requestHint: 'Questi documenti sono ricevute di Stripe. Per la fattura elettronica scrivici.',
    /** Pulsante che apre l'email di richiesta. */
    request: 'Chiedi la fattura',
    /** Oggetto dell'email. */
    requestSubject: 'Richiesta fattura institorAI',
    /** Testo dell'email, da completare. */
    requestBody:
      'Ciao,\nchiedo la fattura per questo pagamento.\n\nData e importo del pagamento:\nNome e cognome o ragione sociale:\nCodice fiscale o partita IVA:\nIndirizzo:\nCodice destinatario o PEC (se li hai):\n',
  },
  /** Pagina di esito dopo il Checkout. */
  result: {
    /** Titolo della pagina. */
    title: 'Esito del pagamento',
    /** Verifica in corso. */
    checking: 'Verifica del pagamento…',
    /** Indirizzo senza riferimento. */
    missing: 'Manca il riferimento del pagamento. Riparti da «Abbonamento».',
    /** Verifica non riuscita. */
    failed: 'Verifica del pagamento non riuscita. Riprova.',
    /** Pulsante per ripetere la verifica. */
    retry: 'Riprova',
    /** Pulsante di ritorno. */
    back: 'Torna ai pagamenti',
    /** Abbonamento attivato. */
    subscriptionDone: 'Abbonamento {{plan}} attivo',
    /** Che cosa cambia. */
    subscriptionDoneHint: 'I limiti del nuovo piano valgono da subito.',
    /** Minuti accreditati. */
    minutesDone: '{{minutes}} minuti di voce aggiunti',
    /** Saldo dopo l'accredito. */
    minutesDoneHint: 'Li trovi fra i minuti disponibili.',
    /** Pagamento in attesa di conferma. */
    pending: 'Pagamento in attesa di conferma',
    /** Che cosa succede dopo. */
    pendingHint: 'Il piano o i minuti arrivano appena Stripe conferma il pagamento.',
    /** Checkout non completato. */
    open: 'Pagamento non completato',
    /** Che cosa fare. */
    openHint: 'Non ti è stato addebitato nulla. Puoi riprovare quando vuoi.',
    /** Sessione scaduta. */
    expired: 'Pagamento scaduto',
  },
};

/** Forma dei testi dei pagamenti, che le altre lingue devono rispettare. */
export type Billing = typeof billing;
